<script lang="ts">
	import type { CourseLessonStore } from '$houdini';
	import LessonOverlayEnd from '$lib/components/Video/Overlays/LessonOverlayEnd.svelte';
	import PlayerGuard from '$lib/components/Video/PlayerGuard.svelte';
	import VideoPlayer from '$lib/components/Video/VideoPlayer.svelte';
	import { page } from '$app/stores';
	import { getCourseNavigationBySlug, gotoCourse, gotoNextLesson } from '$lib/service/navigation';
	import LessonNavigation from '$lib/components/Lesson/LessonNavigation.svelte';
	import LessonDescription from '$lib/components/Lesson/LessonDescription.svelte';
	import LessonCourseBar from '$lib/components/Lesson/LessonCourseBar.svelte';
	import { derived, get } from 'svelte/store';
	import { activityService, type ActivityType } from '$lib/service/supabase/activity.service';
	import MetaHead from '$lib/components/SEO/MetaHead.svelte';
	import { buildVideoSchema } from '../../../../lib/schemaOrg';
	import { onMount } from 'svelte';
	export let data: { CourseLesson: CourseLessonStore };

	let playerHeight: number = 0;
	let videoStartTime: number = 0;

	$: ({ CourseLesson } = data);
	let canAccess: boolean = false;

	const navi = derived([page, data.CourseLesson], ([pageData, courseLessons]) => {
		let slug = pageData.params.slug;
		if (!slug) {
			return undefined;
		}
		return getCourseNavigationBySlug(courseLessons.data?.course, slug);
	});

	const handleNextLessonClicked = () => {
		gotoNextLesson(get(navi));
	};

	const handleActivity = async (data: {
		activity_type: ActivityType;
		currentTime: number;
		heartbeat_interval_ms?: number;
	}) => {
		const { activity_type, currentTime, heartbeat_interval_ms } = data;

		const last_video_position_ms = Math.floor(currentTime * 1000);

		const user_id = $page.data.session?.user?.id;
		const lesson_id = $navi?.currentLesson?.id;
		const lesson_slug = $navi?.currentLesson?.slug || undefined;
		const lesson_name = $navi?.currentLesson?.name || undefined;
		const course_id = $navi?.currentCourse?.id;
		const course_slug = $navi?.currentCourse?.slug || undefined;
		const course_name = $navi?.currentCourse?.name || undefined;

		if (user_id && lesson_id && course_id) {
			await activityService.createViewActivity({
				user_id,
				activity_type,
				last_video_position_ms,
				lesson_id,
				lesson_slug,
				lesson_name,
				course_id,
				course_slug,
				course_name,
				heartbeat_interval_ms: heartbeat_interval_ms || -1
			});
		}
	};

	onMount(async () => {
		console.log('starting request to get last video position');
		const userId = $page.data.session?.user?.id;
		const lessonId = $navi?.currentLesson?.id;
		if (userId && lessonId) {
			videoStartTime = await activityService.getLastVideoPosition(userId, lessonId);
		}
	});
</script>

<MetaHead
	title={$CourseLesson?.data?.course?.name}
	description={$CourseLesson?.data?.course?.shortDescription}
	metaTitle={$CourseLesson?.data?.course?.seo?.metaTitle}
	metaDescription={$CourseLesson?.data?.course?.seo?.metaDescription}
	ogUrl={$page.url.toString()}
	ogTitle={$CourseLesson?.data?.course?.seo?.ogTitle}
	ogDescription={$CourseLesson?.data?.course?.seo?.ogDescription}
	ogImage={$CourseLesson?.data?.course?.seo?.ogImage?.url}
	image={$CourseLesson?.data?.course?.featuredThumbnail?.url}
	useSchemaOrg={true}
	schema={buildVideoSchema($CourseLesson?.data?.course)}
/>

<div class="w-full flex flex-col">
	<LessonCourseBar course={$navi?.currentCourse} on:back={() => gotoCourse($navi)} />
	<div class="p-6">
		<div class="w-full grid grid-cols-3 gap-8 my-auto">
			<div class="col-span-3 md:col-span-2">
				<VideoPlayer
					autoplay
					on:playerheight={({ detail }) => {
						playerHeight = detail;
					}}
					on:activity={({ detail }) => handleActivity(detail)}
					playbackId={canAccess ? $navi?.currentLesson?.streamUrl : ''}
					id={$navi?.currentLesson?.id}
					title={$navi?.currentLesson?.name}
					hasOverlayEnd={true}
					{videoStartTime}
				>
					<LessonOverlayEnd
						slot="overlay-end"
						on:click={handleNextLessonClicked}
						nextLesson={$navi?.nextLesson}
					/>

					<PlayerGuard
						slot="overlay-guard"
						bind:canAccess
						courseLesson={$CourseLesson.data}
						lessonSlug={$page.params?.slug}
					/>
				</VideoPlayer>
			</div>

			<LessonNavigation
				additionalClass="col-span-3 md:col-span-1"
				additionalStyle="height: {playerHeight}px;"
				courseNavigation={$navi}
			/>
			{#if $navi?.currentLesson}
				<LessonDescription
					additionalClass="col-span-3 md:col-span-2"
					lesson={$navi.currentLesson}
				/>
			{/if}
		</div>
	</div>
</div>
